import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Grid, Stack } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import {
  ClosureDTO,
  ICandidate,
  Field,
  FieldTypeEnum,
  getAllClosureCustomFields,
  ClientDTO,
  getPlacementOriginList,
  getPlacementStatusList,
  PlacementOriginDTO,
  PlacementStatusDTO,
} from 'wisestep-api';
import {
  ControlledTextArea,
  ControlledTextInput,
  Label,
  WsButton,
  WsDatePicker,
  WsLocationSearch,
  WsSelect,
  WsSelectObj,
} from 'wisestep-base';
import { date, object, string } from 'yup';

import { ClientAutoCompleteFilter } from '../../../components';
import { SelectBusinessUnit } from '../../../components/SelectBusinessUnit';
import WsCheckBox from '../../../components/WsCheckBox/WsCheckBox';
import { useWorkspaceContext } from '../../../context';
import { useClientAuthz, ClientAction } from '../../../hooks/useClientAuthz';
import CustomField from '../../clientListingPage/components/CustomField';
import CreateNewClientDialog from '../../jobs/post/components/CreateNewClientDialog';
import { isEligibleForWisestepCandidates } from '../helper/ClosureHelper';

const validationSchema = object().shape({
  clientName: string().required('Please provide the client name'),
  candidateName: string().required('Please provide the candidate name'),
  candidateLocation: object()
    .typeError('Please provide the candidate location')
    .required('Please provide the candidate location'),
  candidateEmail: string()
    .email('Candidate email must be a valid email')
    .required('Please provide the Email ID of candidate'),
  joiningDate: date().nullable(false).required('Please provide joining date'),
});

export interface IComponentProps {
  formWizardSubMenuItemClick: (index: number) => void;
  setClosureDTO: (data: ClosureDTO) => void;
  currentClosureDTO: ClosureDTO;
  setDirtyFieldsValue: (data: any) => void;
  getDirtyFieldsValue: any;
  onCancel: () => void;
  setCandidateFromSubmission: (data: any) => void;
  placementType: string;
}

export interface IRecruiterProps {
  text: string | null;
  email: string | null;
  id?: string;
  name: string | null;
}

const ClosureCandidateDetails = ({
  formWizardSubMenuItemClick,
  setClosureDTO,
  currentClosureDTO,
  setDirtyFieldsValue,
  setCandidateFromSubmission,
  onCancel,
  placementType,
}: IComponentProps) => {
  const workspace = useWorkspaceContext();
  const location: any = useLocation();
  const [openNewClientDialogue, setOpenNewClientDialogue] = useState<boolean>(false);
  const [companyNameNewVal, setCompanyNameNewVal] = useState<string>('');

  const isAuthorizedAddClient = useClientAuthz(ClientAction.ADD_CLIENT_DETAILS);

  const getDefaultValues = (): ClosureDTO => {
    const responseDTO = { ...currentClosureDTO };
    const candidateFromSubmission: ICandidate =
      location.state && location.state.candidate ? location.state.candidate : undefined;
    responseDTO.candidateName = currentClosureDTO.candidateName
      ? currentClosureDTO.candidateName
      : candidateFromSubmission?.name ?? '';
    responseDTO.candidateEmail = currentClosureDTO.candidateEmail
      ? currentClosureDTO.candidateEmail
      : candidateFromSubmission?.email ?? '';
    responseDTO.candidateContact = currentClosureDTO.candidateContact
      ? currentClosureDTO.candidateContact
      : candidateFromSubmission?.phoneNumber ?? '';
    responseDTO.clientName = currentClosureDTO.clientName
      ? currentClosureDTO.clientName
      : candidateFromSubmission?.companyName ?? '';
    responseDTO.clientId = currentClosureDTO.clientId
      ? currentClosureDTO.clientId
      : location.state?.client?.id ?? '';
    responseDTO.candidateLocation = !isEmpty(currentClosureDTO.candidateLocation)
      ? currentClosureDTO.candidateLocation
      : null;
    responseDTO.submissionId = location.state
      ? location.state.submissionId
      : currentClosureDTO.submissionId;
    responseDTO.placementOrigin = currentClosureDTO.placementOrigin;
    responseDTO.placementStatusId = currentClosureDTO.placementStatusId;
    responseDTO.placementStatusReason = currentClosureDTO.placementStatusReason;
    if (candidateFromSubmission) {
      if (
        candidateFromSubmission.jobAccountManagerName &&
        candidateFromSubmission.jobAccountManagerEmail
      ) {
        const accountManager: IRecruiterProps = {
          name: candidateFromSubmission.jobAccountManagerName,
          text:
            candidateFromSubmission.jobAccountManagerName +
            ' (' +
            candidateFromSubmission.jobAccountManagerEmail +
            ')',
          email: candidateFromSubmission.jobAccountManagerEmail,
        };
        responseDTO.accountManager = JSON.stringify(accountManager);
      }
      setCandidateFromSubmission(candidateFromSubmission);
    }
    return responseDTO;
  };

  const defaultValues = getDefaultValues();
  const methods = useForm<ClosureDTO>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      customFields: defaultValues.customFields ?? {},
    },
  });

  const {
    setValue,
    watch,
    formState: { dirtyFields },
  } = methods;

  const [ourPlacementStatusOptions, setOurPlacementStatusOptions] = useState<WsSelectObj[]>([]);
  const [ourPlacementOriginOptions, setOurPlacementOriginOptions] = useState<WsSelectObj[]>([]);
  const [defaultPlacementStatusId, setDefaultPlacementStatusId] = useState<string>('');
  const [customFields, setCustomFields] = useState<Field[]>();

  useEffect(() => {
    (async () => {
      const { data } = await getAllClosureCustomFields();
      setCustomFields(data);
    })();
  }, []);

  const clientName = watch('clientName') ?? '';
  const onSubmit = (data: ClosureDTO) => {
    if (!data.placementStatusId) {
      data.placementStatusId = defaultPlacementStatusId;
    }
    setDirtyFieldsValue(dirtyFields);
    data.clientName = clientName?.trim();
    if (clientName.includes('->')) {
      data.clientName = clientName?.split('->')[1]?.trim();
    }
    setClosureDTO(data);
    formWizardSubMenuItemClick(1);
  };

  const onClientCreateFromDialogue = (selectedClient: ClientDTO) => {
    setValue('clientName', selectedClient?.name);
    setValue('clientId', selectedClient.id);
    setOpenNewClientDialogue(false);
  };

  useEffect(() => {
    const getOurPlacementStatusList = async () => {
      const response = await getPlacementStatusList();
      const optionsArray: WsSelectObj[] = [];
      response.data.forEach((obj: PlacementStatusDTO) => {
        optionsArray.push({ label: obj.placementStatusName as string, value: obj.id ?? '' });
      });
      const statusDefaultId = optionsArray.find((e) => e.label === 'Placed');
      setOurPlacementStatusOptions(optionsArray);
      setDefaultPlacementStatusId(statusDefaultId?.value as string);
    };

    const getAllPlacementOrigins = async () => {
      const response = await getPlacementOriginList();
      const optionsArray: WsSelectObj[] = [];
      response.data.forEach((obj: PlacementOriginDTO) => {
        if (obj.visibility) {
          optionsArray.push({ value: obj.id ?? '', label: obj.originName as string });
        }
      });
      setOurPlacementOriginOptions(optionsArray);
    };
    getOurPlacementStatusList();
    getAllPlacementOrigins();
  }, []);

  return customFields ? (
    <>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Box borderRadius={1.5}>
            <Grid container py={1}>
              <Grid item p={1} sm={5.75}>
                <>
                  {isAuthorizedAddClient ? (
                    <ClientAutoCompleteFilter
                      key={clientName}
                      name="clientName"
                      isPlacement={true}
                      clientIdFieldName="clientId"
                      textForAddingNew="Client"
                      showMandatorySymbol
                      onNewValueCreate={(data: string) => {
                        setValue('clientName', ' ');
                        setCompanyNameNewVal(data ?? '');
                        setOpenNewClientDialogue(true);
                      }}
                    />
                  ) : (
                    <ClientAutoCompleteFilter
                      key={clientName}
                      name="clientName"
                      isPlacement={true}
                      clientIdFieldName="clientId"
                      showMandatorySymbol
                    />
                  )}
                </>
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateName"
                  type="text"
                  fullWidth
                  labelText="Candidate Name"
                  variant="outlined"
                  showMandatorySymbol
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsLocationSearch
                  size="small"
                  placeHolder="Candidate Location"
                  name="candidateLocation"
                  variant="outlined"
                  multiSelect={false}
                  showMandatorySymbol={true}
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateEmail"
                  type="text"
                  fullWidth
                  labelText="Candidate Email"
                  variant="outlined"
                  showMandatorySymbol
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={5.75}>
                <ControlledTextInput
                  name="candidateContact"
                  type="text"
                  fullWidth
                  labelText="Candidate Contact"
                  variant="outlined"
                  disabled={currentClosureDTO.id ? true : false}
                />
              </Grid>
              <Grid item p={1} sm={placementType === 'PERMANENT' ? 5.75 : 3.83}>
                <Label>Offer Date</Label>
                <WsDatePicker name="offerDate" label="" />
              </Grid>
              <Grid item p={1} sm={placementType === 'PERMANENT' ? 5.75 : 3.83}>
                <Label>Joining Date*</Label>
                <WsDatePicker name="joiningDate" label="" required={true} />
              </Grid>
              {placementType === 'CONTRACT' && (
                <>
                  <Grid item p={1} sm={3.83}>
                    <Label>End Date*</Label>
                    <WsDatePicker name="endDate" label="" required={true} />
                  </Grid>
                  <Grid item p={1} sm={3.83}>
                    <ControlledTextInput
                      name="noticePeriod"
                      type="text"
                      fullWidth
                      labelText="Notice Period"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item p={1} sm={3.83}>
                    <ControlledTextInput
                      name="renewalPeriod"
                      type="text"
                      fullWidth
                      labelText="Renewal Period"
                      variant="outlined"
                    />
                  </Grid>
                  <Grid item p={1} sm={3.83}>
                    <WsSelect label="Vendor Name" name="vendor" selectArray={[]} size="small" />
                  </Grid>
                </>
              )}
              <Grid item p={1} sm={5.75}>
                <SelectBusinessUnit name="businessUnit" />
              </Grid>
              <Grid item p={1} sm={5.75}>
                {defaultPlacementStatusId && (
                  <WsSelect
                    label="Placement Status Name"
                    name="placementStatusId"
                    selectArray={ourPlacementStatusOptions}
                    defaultValue={defaultPlacementStatusId}
                    size="small"
                  />
                )}
              </Grid>
              <Grid item p={1} sm={5.75}>
                <WsSelect
                  label="Placement Origin"
                  name="placementOrigin"
                  selectArray={ourPlacementOriginOptions}
                  size="small"
                />
              </Grid>
              {customFields.map(
                (field) =>
                  field.visible && (
                    <Grid
                      item
                      key={field.id}
                      p={1}
                      sm={field.type === FieldTypeEnum.Checkbox ? 11.5 : 5.75}
                    >
                      <CustomField {...field} name={`customFields.${field.name}`} />
                    </Grid>
                  )
              )}
              <Grid item p={1} sm={5.75}></Grid>
              {workspace.id && isEligibleForWisestepCandidates(workspace.id) && (
                <Grid item p={1} sm={5.75}>
                  <WsCheckBox label="Wisestep Candidate" name="wisestepCandidate" bold={true} />
                </Grid>
              )}
              {placementType === 'CONTRACT' && (
                <Grid item p={1} sm={11.5}>
                  <ControlledTextArea
                    name="paymentTermDescription"
                    defaultValue={''}
                    minRows={8}
                    placeholder="Payment Terms Description"
                  />
                </Grid>
              )}
            </Grid>
          </Box>
          <Stack
            p={1}
            spacing={2}
            direction="row"
            sx={{ display: 'flex', justifyContent: 'right' }}
          >
            <WsButton variant="outlined" size="medium" type="button" onClick={onCancel}>
              {`Cancel`}
            </WsButton>
            <WsButton variant="contained" size="medium" type="submit">
              {`Next`}
            </WsButton>
          </Stack>
        </form>
      </FormProvider>
      {openNewClientDialogue && (
        <CreateNewClientDialog
          open={openNewClientDialogue}
          clientNameInputVal={companyNameNewVal}
          onClose={setOpenNewClientDialogue}
          onSelect={onClientCreateFromDialogue}
        />
      )}
    </>
  ) : null;
};

export default ClosureCandidateDetails;
